import * as Effect from 'effect/Effect';
import * as Context from 'effect/Context';
import * as Layer from 'effect/Layer';
import { pipe } from 'effect/Function';
import type {
  AuthResponse,
  LoginCredentials,
  RegisterData,
} from '@/api/schemas/user.schema';

export class AuthService extends Context.Tag('AuthService')<
  AuthService,
  {
    readonly login: (credentials: LoginCredentials) => Effect.Effect<AuthResponse, any>;
    readonly register: (data: RegisterData) => Effect.Effect<AuthResponse, any>;
    readonly refreshToken: (refreshToken: string) => Effect.Effect<string, any>;
    readonly logout: () => Effect.Effect<void, never>;
  }
>() {}

export const AuthServiceLive = Layer.effect(
  AuthService,
  Effect.gen(function* (_) {
    // The real backend auth contract is an email OTP flow:
    //   POST /auth/login { email }              -> sends a one-time code
    //   POST /auth/verify-login { email, otp }  -> returns the JWT
    // The login page (pages/auth/login.tsx) drives that flow directly. The
    // password-style login/register/refresh below never matched this backend
    // (they posted to routes that don't exist), so they now fail explicitly
    // with an honest message instead of surfacing confusing 404s.
    return {
      login: () =>
        Effect.fail(
          new Error('Password login is not supported — sign in with the email code flow')
        ),

      register: () =>
        Effect.fail(
          new Error('Registration is closed — accounts are provisioned manually')
        ),

      refreshToken: () =>
        Effect.fail(
          new Error('Token refresh is not supported — tokens are long-lived; sign in again when expired')
        ),

      logout: () =>
        pipe(
          Effect.succeed(undefined),
          Effect.tap(() => Effect.log('User logged out'))
        ),
    };
  })
);
